import FilterListIcon from '@mui/icons-material/FilterList';
import { Box } from '@mui/material';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';

import { ClearFiltersButton, FilterSelect, InfiniteScrollSelect, ViewSwitcher } from '../../../components/_mui';
import { FilterTypesEnum, ViewTypesEnum } from '../../../types/shell';
import { IFilterItem, IFiltersRef } from '../../../types/tables';

interface IProps {
  renderSearchField: React.JSX.Element | null;
  filterConfig: IFilterItem[];
  activeView: ViewTypesEnum;
  setActiveView: (view: ViewTypesEnum) => void;
  onSearchReset: () => void;
  query?: string;
  selectedItemName?: string;
  setSelectedItemName?: (name: string | undefined) => void;
  selectedCampaignName?: string;
  setSelectedCampaignName?: (name: string | undefined) => void;
  renderDownloadButton: React.JSX.Element | null;
  renderAddButton: React.JSX.Element | null;
}

const topBarStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  flexDirection: { xs: 'column' },
  gap: { xs: 2 },
  '@media (min-width: 730px)': {
    flexDirection: 'row',
    gap: 0,
  },
};

const filtersRowStyles = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  justifyContent: 'space-between',
  alignItems: { xs: 'stretch', md: 'center' },
  mt: { xs: 2, md: 3 },
  gap: { xs: 2, md: 0 },
};

const filterListStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: { xs: 1.5, sm: 3 },
  overflowX: { xs: 'auto', sm: 'visible' },
  pb: { xs: 1, sm: 0 },
};

const Filters = forwardRef<IFiltersRef, IProps>(
  (
    {
      renderDownloadButton,
      renderAddButton,
      renderSearchField,
      filterConfig,
      activeView,
      setActiveView,
      onSearchReset,
      query,
      selectedItemName,
      setSelectedItemName,
      selectedCampaignName,
      setSelectedCampaignName,
    }: IProps,
    filtersRef,
  ) => {
    const isAnyFilterActive = useMemo(() => {
      return filterConfig.some((filter) => filter.value && filter?.value?.length > 0) || (query && query?.length > 0);
    }, [filterConfig, query]);

    const handleClearFilter = useCallback((onChange: (value: string | string[] | undefined) => void) => {
      onChange('');
    }, []);

    const handleClearAllFilters = useCallback(() => {
      filterConfig.forEach((filter) => {
        filter.onChange('');
      });

      if (onSearchReset) {
        onSearchReset();
      }
    }, [filterConfig, onSearchReset]);

    useImperativeHandle(
      filtersRef,
      () => ({
        resetFilters: handleClearAllFilters,
      }),
      [handleClearAllFilters],
    );

    const renderControls = useMemo(() => {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {renderDownloadButton}
          {renderAddButton}
        </Box>
      );
    }, [renderDownloadButton, renderAddButton]);

    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={topBarStyles}>
          {renderSearchField}
          {renderControls}
        </Box>
        <Box sx={filtersRowStyles}>
          <Box sx={filterListStyles}>
            <FilterListIcon color="disabled" />
            {filterConfig.map(
              ({
                tabId,
                label,
                value,
                onChange,
                options,
                isMultiple = false,
                isVisible = true,
                limit,
                onLimitReached,
                useInfiniteScroll = false,
                autocompleteConfig,
                dataHook,
                dataAction,
              }) => {
                const isItemFilter = tabId === FilterTypesEnum.Item;
                const handleName = isItemFilter ? setSelectedItemName : setSelectedCampaignName;
                const name = isItemFilter ? selectedItemName : selectedCampaignName;
                const isInfiniteScrollSelector = useInfiniteScroll && autocompleteConfig && dataHook;

                return isVisible ? (
                  isInfiniteScrollSelector ? (
                    <InfiniteScrollSelect
                      key={tabId}
                      label={label}
                      value={value}
                      onChange={onChange}
                      onClear={handleClearFilter}
                      onNameChange={handleName}
                      displayName={name}
                      config={autocompleteConfig}
                      useDataHook={dataHook}
                    />
                  ) : (
                    <FilterSelect
                      limit={limit}
                      onLimitReached={onLimitReached}
                      isMultiple={isMultiple}
                      key={tabId}
                      label={label}
                      value={value}
                      onChange={onChange}
                      options={options}
                      onClear={handleClearFilter}
                      dataAction={dataAction}
                    />
                  )
                ) : null;
              },
            )}
            {isAnyFilterActive && <ClearFiltersButton onClick={handleClearAllFilters} />}
          </Box>
          <ViewSwitcher activeView={activeView} setActiveView={setActiveView} />
        </Box>
      </Box>
    );
  },
);

export default Filters;
